import React from 'react';
import {
  DEFAULT_PICKUP_CAPACITY,
  InputPickupCapacity,
} from '@components/forms/InputPickupCapacity';
import { InputSelect } from '@components/forms/InputSelect';
import { InputTax } from '@components/forms/InputTax';
import { InputSwitch, useFormContext } from '@goodfynd/forms';
import { ReviewCheckBoxField, ReviewSummaryContainer, ReviewTextField } from '@goodfynd/forms';
import { FlexView } from '@goodfynd/ui';
import useVendorBusinessInfo from '@hooks/useVendorBusinessInfo';
import useVendorProductGroups from '@hooks/useVendorProductGroups';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { stringUtil } from 'utils/index';

import type { ScheduleWizardFields } from './types';

export function StepOrdering() {
  const { control, setValue, watch } = useFormContext<ScheduleWizardFields>();
  const vendorId = watch('vendorId');
  const { businessInfo } = useVendorBusinessInfo({ vendorId });
  const { productGroups } = useVendorProductGroups({ vendorId });
  const orderingEnabled = (watch('pickupCapacity') ?? 0) > 0;

  return (
    <>
      <InputSwitch
        defaultValue={false}
        label="Private event"
        name="isPrivate"
        {...{ control }}
      />

      <FormControlLabel
        label="Allow online orders"
        control={
          <Switch
            checked={orderingEnabled}
            disabled={!businessInfo.orderingEnabled}
            onChange={(event) => {
              const value = event.target.checked ? DEFAULT_PICKUP_CAPACITY : 0;
              setValue('pickupCapacity', value);
              setValue('pickupCapacityOption', { value });
            }}
          />
        }
      />
      {orderingEnabled && (
        <>
          <InputPickupCapacity<ScheduleWizardFields>
            control={control}
            name="pickupCapacityOption"
          />
          {businessInfo && !!watch('pickupCapacityOption')?.value && (
            <InputTax<ScheduleWizardFields>
              control={control}
              css={{ marginTop: 8 }}
              name="taxPercentOption"
            />
          )}
        </>
      )}

      <InputSelect
        control={control}
        defaultValue={businessInfo.defaultMenu}
        label="Menu served"
        name="menuId"
        required
        options={productGroups.map((group) => ({
          value: group.id,
          label: group.label,
        }))}
      />
    </>
  );
}

export function StepOrderingSummary() {
  const { getValues } = useFormContext<ScheduleWizardFields>();
  const {
    pickupCapacityOption = { value: 0 },
    taxPercentOption = { value: 0 },
    menuId,
    isPrivate,
    vendorId,
  } = getValues();
  const { productGroups } = useVendorProductGroups({ vendorId });

  return (
    <ReviewSummaryContainer>
      <FlexView gap={32} id="ordering">
        <ReviewTextField
          label="Order pickup capacity"
          value={
            Number(pickupCapacityOption.value) > 0
              ? `${pickupCapacityOption.value} ${stringUtil.pluralize(
                  'order',
                  Number(pickupCapacityOption.value)
                )} per 15 minute period`
              : 'No online ordering'
          }
        />
        <ReviewTextField
          label="Tax percent"
          value={
            Number(taxPercentOption?.value ?? 0) > 0
              ? `${taxPercentOption.value}%`
              : 'No tax'
          }
        />
        <ReviewTextField
          label="Menu"
          value={productGroups.find((menu) => menu.id === menuId)?.label}
        />
        <ReviewCheckBoxField label="Private Event" value={isPrivate} />
      </FlexView>
    </ReviewSummaryContainer>
  );
}
